import i18next from 'i18next';

import { generateDefaultSheet, makeXLS } from '@commons/utils/makeXLS';

const ERROR_CODE_TO_TRANSLATION_KEY = {
  ORDER_ALREADY_INTERNALLY_INVOICED:
    'ORDERS.ORDERS.PARTNER_INVOICE_ERROR_ALREADY_INTERNALLY_INVOICED',
  ORDER_STATUS_BELOW_MINIMUM: 'ORDERS.ORDERS.PARTNER_INVOICE_ERROR_STATUS_BELOW_MINIMUM',
  ALREADY_LINKED_PARTNER_INVOICE: 'ORDERS.ORDERS.PARTNER_INVOICE_ERROR_ALREADY_PARTNER',
  ALREADY_LINKED_INTERNAL_INVOICE: 'ORDERS.ORDERS.PARTNER_INVOICE_ERROR_ALREADY_INTERNAL',
  INVALID_VAT_RATE: 'ORDERS.ORDERS.PARTNER_INVOICE_ERROR_INVALID_VAT_RATE',
  NO_BILLABLE_LINES: 'ORDERS.ORDERS.PARTNER_INVOICE_ERROR_NO_BILLABLE_LINES',
  MISSING_STORE_MAPPING: 'ORDERS.ORDERS.PARTNER_INVOICE_ERROR_MISSING_STORE_MAPPING',
};

const _formatInvalidVatRateMessage = (error) => {
  const name = error.supplierProductName ?? '';
  const sku = error.supplierProductSku ?? '';

  if (name && sku) {
    return i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_INVALID_VAT_RATE_WITH_DETAILS', {
      name,
      sku,
    });
  }

  if (name) {
    return i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_INVALID_VAT_RATE_WITH_NAME', {
      name,
    });
  }

  return i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_INVALID_VAT_RATE');
};

const _formatAlreadyLinkedPartnerMessage = (error) => {
  if (error.partnerInvoiceReference) {
    return i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_ALREADY_PARTNER_WITH_REFERENCE', {
      reference: error.partnerInvoiceReference,
    });
  }

  return i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_ALREADY_PARTNER');
};

const _formatAlreadyLinkedInternalMessage = (error) => {
  if (error.internalInvoiceNumber) {
    return i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_ALREADY_INTERNAL_WITH_NUMBER', {
      number: error.internalInvoiceNumber,
    });
  }

  return i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_ALREADY_INTERNAL');
};

const _formatErrorMessage = (error) => {
  if (error.errorCode === 'INVALID_VAT_RATE') {
    return _formatInvalidVatRateMessage(error);
  }

  if (error.errorCode === 'ALREADY_LINKED_PARTNER_INVOICE') {
    return _formatAlreadyLinkedPartnerMessage(error);
  }

  if (error.errorCode === 'ALREADY_LINKED_INTERNAL_INVOICE') {
    return _formatAlreadyLinkedInternalMessage(error);
  }

  const translationKey = ERROR_CODE_TO_TRANSLATION_KEY[error.errorCode];

  return translationKey ? i18next.t(translationKey) : error.errorCode;
};

/**
 * Generates and downloads an XLSX file listing partner invoice prerequisite errors.
 *
 * @param {Array<{ orderId: string, errorCode: string, supplierProductName?: string, supplierProductSku?: string, partnerInvoiceReference?: string, internalInvoiceNumber?: string }>} errors
 * @param {Object} ordersById - Map of orderId → order object (must have a `reference` field)
 */
export const exportPartnerInvoiceErrors = (errors, ordersById) => {
  const rows = errors.map((error) => ({
    reference: ordersById[error.orderId]?.reference ?? '',
    error: _formatErrorMessage(error),
  }));

  const columns = [
    { propertyKey: 'reference', name: i18next.t('GENERAL.REFERENCE') },
    { propertyKey: 'error', name: i18next.t('GENERAL.ERROR') },
  ];

  const sheet = generateDefaultSheet(
    i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_FILE_SHEET_NAME'),
    columns,
    rows,
  );

  makeXLS(i18next.t('ORDERS.ORDERS.PARTNER_INVOICE_ERROR_FILE_TITLE'), [sheet]);
};
